/**
 * WelcomeStep — first step of the onboarding wizard.
 *
 * Pure educational screen: headline, 3-sentence explainer, animated diagram,
 * and a CTA that advances to the Organization step. No form inputs.
 *
 * The diagram is rendered by `SpoofioDiagram`, which is also reused on the
 * /signup hero panel so the narrative carries through from signup to wizard.
 */

import type { RefObject } from 'react'
import { Button } from '@/components/ui'
import { SpoofioDiagram } from '@/components/branding/SpoofioDiagram'

type WelcomeStepProps = {
  onNext: () => void
  headingRef?: RefObject<HTMLHeadingElement>
}

export function WelcomeStep({ onNext, headingRef }: WelcomeStepProps) {
  return (
    <section
      role="form"
      aria-label="Welcome step"
      className="max-w-2xl mx-auto text-center space-y-8 py-12"
    >
      <h2
        ref={headingRef}
        tabIndex={-1}
        className="text-4xl font-bold text-gray-900 tracking-tight focus:outline-none"
      >
        Protect your web pages from phishing
      </h2>

      <p className="text-lg text-gray-600 leading-relaxed">
        Spoofio plants an invisible marker on your web pages. If someone copies
        your page to a fake website, the marker phones home and alerts you
        instantly. You&apos;ll know exactly which page was copied, where it
        appeared, and who visited it.
      </p>

      <SpoofioDiagram />

      <div className="pt-4">
        <Button size="lg" onClick={onNext} className="w-full sm:w-auto min-h-[44px]">
          Let&apos;s get started
        </Button>
      </div>
    </section>
  )
}
